import { Dialog } from '@mui/material'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import type { CSSProperties } from 'react'
import { createRoot } from 'react-dom/client'

import type { AlertPresenter } from './alertTypes'
import type { BrandingThemeType, Config } from './configTypes'

export interface PopupAppearance {
  branding: Config['branding']
  mode: BrandingThemeType
}

type PopupPresenter = (
  ...args: [...alert: Parameters<AlertPresenter>, appearance?: PopupAppearance]
) => ReturnType<AlertPresenter>

const createPopupTheme = (appearance?: PopupAppearance) => {
  if (!appearance) return createTheme()
  const { branding, mode } = appearance
  const { colorStyles, typography, constants } = branding
  return createTheme({
    palette: {
      mode,
      primary: { main: colorStyles.primary[mode], contrastText: colorStyles.onPrimary[mode] },
      secondary: { main: colorStyles.secondary[mode], contrastText: colorStyles.onSecondary[mode] },
      error: { main: colorStyles.error[mode], contrastText: colorStyles.onError[mode] },
      background: { default: colorStyles.background[mode], paper: colorStyles.surface[mode] },
      text: { primary: colorStyles.onSurface[mode], secondary: colorStyles.onSurfaceVariant[mode] },
      divider: colorStyles.outlineVariant[mode],
    },
    typography: { fontFamily: `${JSON.stringify(typography.iosDefaultFontFamily)}, sans-serif` },
    shape: { borderRadius: constants.cornersRadius },
  })
}

export const showPopup: PopupPresenter = (title, primaryButton, message, secondaryButton, appearance) => {
  const popupTheme = createPopupTheme(appearance)
  const constants = appearance?.branding.constants
  const textStyleFor = (name: string, fallbackWeight: number): CSSProperties => {
    const font = appearance?.branding.typography.fontStyles?.find(el => el.styleName === name)?.ios
    return {
      fontFamily: popupTheme.typography.fontFamily,
      fontSize: `${font?.fontSize ?? 17}px`,
      fontWeight: font?.fontWeight ?? fallbackWeight,
      fontStyle: font?.fontStyle ?? 'normal',
      letterSpacing: font ? `${font.letterSpacing}px` : undefined,
      lineHeight: font ? `${font.lineHeight}%` : '130%',
    }
  }
  const textStyle = textStyleFor('Body', 400)
  const titleStyle = textStyleFor('Title 3 Bold', 600)
  const buttonStyle: CSSProperties = {
    ...textStyle,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    minHeight: constants?.buttonHeight ?? 50,
    padding: `0 ${constants?.horizontalSpacing ?? 16}px`,
    border: 0,
    borderRadius: popupTheme.shape.borderRadius,
    cursor: 'pointer',
  }
  return new Promise<boolean>(resolve => {
    const container = document.createElement('div')
    document.body.appendChild(container)
    const root = createRoot(container)
    const titleId = `alert-${crypto.randomUUID()}`
    const messageId = `${titleId}-message`
    const close = (confirmed: boolean) => {
      root.unmount()
      container.remove()
      resolve(confirmed)
    }
    root.render(
      <ThemeProvider theme={popupTheme}>
        <Dialog
          open
          fullWidth
          maxWidth="xs"
          aria-labelledby={titleId}
          aria-describedby={message ? messageId : undefined}
          onClose={() => close(false)}
          slotProps={{ paper: { sx: { borderRadius: `${popupTheme.shape.borderRadius}px`, backgroundImage: 'none' } } }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: constants?.verticalSpacing ?? 16,
              padding: `${constants?.horizontalMargin ?? 24}px`,
              borderRadius: constants?.cornersRadius ?? 8,
            }}
          >
            <h2 id={titleId} style={{ ...titleStyle, margin: 0 }}>
              {title}
            </h2>
            {message && (
              <p id={messageId} style={{ ...textStyle, color: popupTheme.palette.text.secondary, margin: 0 }}>
                {message}
              </p>
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                flexWrap: 'wrap',
                gap: constants?.horizontalSpacing ?? 16,
              }}
            >
              {secondaryButton && (
                <button
                  type="button"
                  autoFocus
                  style={{ ...buttonStyle, background: 'transparent', color: popupTheme.palette.primary.main }}
                  onClick={() => close(false)}
                >
                  {secondaryButton}
                </button>
              )}
              <button
                type="button"
                autoFocus={!secondaryButton}
                style={{
                  ...buttonStyle,
                  background: popupTheme.palette.primary.main,
                  color: popupTheme.palette.primary.contrastText,
                }}
                onClick={() => close(true)}
              >
                {primaryButton}
              </button>
            </div>
          </div>
        </Dialog>
      </ThemeProvider>
    )
  })
}
